import React from 'react';
import { submitErrorReport } from '../../services/errorReportService';
import i18n from '../../i18n';

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  reported: boolean;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null, reported: false };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Unhandled app error:', error, errorInfo);
    submitErrorReport(
      'crash',
      error.message || 'Unknown React crash',
      error.stack,
      errorInfo?.componentStack,
    ).then(() => {
      this.setState({ reported: true });
    }).catch(() => {
      this.setState({ reported: true });
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const font = { fontFamily: '"Courier New", Courier, monospace' } as const;

      return (
        <div className="min-h-screen bg-[var(--bg-base)] text-[var(--text-primary)] flex items-center justify-center p-6">
          <div className="max-w-md w-full rounded-3xl border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] p-6 shadow-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-emerald-400" style={font}>Fricamp</p>
            <h1 className="mt-3 text-2xl font-bold uppercase tracking-wider" style={font}>{i18n.t("auto.common.errorboundary.appen_traff_en_feil", "Appen traff en feil")}</h1>
            <p className="mt-3 text-sm text-[var(--text-secondary)]" style={font}>
              {i18n.t("auto.common.errorboundary.prov_a_laste_inn_appen_pa_nytt", "Prøv å laste inn appen på nytt. Feilrapporten er sendt automatisk.")}
            </p>
            <div className="mt-5 flex gap-3 items-center">
              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex rounded-xl bg-emerald-700 hover:bg-emerald-600 px-4 py-2 text-sm font-bold uppercase tracking-wider text-emerald-50 border border-emerald-500"
                style={font}
              >
                {i18n.t("auto.common.errorboundary.last_inn_pa_nytt", "Last inn på nytt")}
              </button>
              {this.state.reported && (
                <span className="text-2xs font-bold uppercase tracking-widest text-emerald-400/70" style={font}>
                  {i18n.t("auto.common.errorboundary.rapport_sendt", "Rapport sendt")}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
